// @ts-nocheck
// Team performance, built from the roster this dashboard already has —
// `team-lead-stats` (via useTeamRealtime), joined onto the live agent list.
// `dashboard-stats/agent-call-stats` looked like the natural date-range
// source (it's the only agent-wise endpoint that takes start_date/end_date)
// but it's a plain admin-dashboard widget with no team-lead scoping: a
// `role_ccagent` JWT (what a team lead's token actually carries — a team
// lead here is an agent record with an `access: 'team_lead'` flag, not a
// distinct backend role) gets a flat 401 from it. `team-lead-stats` is the
// one endpoint actually gated for this role, so this section is scoped to
// whatever window that endpoint reports (today), not the filter bar's date
// range — same "fixed, not affected by the filter" honesty the agent
// dashboard already applies to its own Quick Periods row.
import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getChartColors } from '@/features/cti/components/call-stats/colors';
import { EmptyNote, HAIRLINE, MiniBar, Panel, TONE, TableShell, Td, Th, nf } from './teamUi';
import { connectTone } from './teamModel';

const CHART_HEIGHT = 260;

const COLUMNS = [
  { key: 'name', label: 'Agent', align: 'left', numeric: false },
  { key: 'total_calls', label: 'Total', align: 'right', numeric: true },
  { key: 'inbound', label: 'Inbound', align: 'right', numeric: true },
  { key: 'outbound', label: 'Outbound', align: 'right', numeric: true },
  { key: 'connected', label: 'Connected', align: 'right', numeric: true },
  { key: 'missed', label: 'Missed', align: 'right', numeric: true },
  { key: 'connectPct', label: 'Connect %', align: 'left', numeric: true },
  { key: 'avg_handle', label: 'Avg handle', align: 'right', numeric: true },
];

function TotalTile({ label, value, tone }) {
  return (
    <div className={`rounded-xl border bg-muted/30 px-3 py-2 ${HAIRLINE}`}>
      <div className="truncate text-[9.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </div>
      <div
        className={`mt-1 truncate text-[18px] font-semibold leading-none tabular-nums ${
          tone ? TONE[tone].text : 'text-foreground'
        }`}
      >
        {nf(value)}
      </div>
    </div>
  );
}

export default function TeamPerformance({ agents, totals, isDark }) {
  const [sort, setSort] = useState({ key: 'total_calls', dir: 'desc' });

  const rows = useMemo(
    () =>
      (agents || [])
        .filter((a) => a.stats)
        .map((a) => ({
          id: a.id,
          name: a.name,
          total_calls: a.stats.total_calls,
          inbound: a.stats.inbound,
          outbound: a.stats.outbound,
          connected: a.stats.connected,
          missed: a.stats.missed,
          connectPct: a.connectPct,
          avg_handle: a.stats.avg_handle,
        })),
    [agents],
  );

  const sorted = useMemo(() => {
    const column = COLUMNS.find((c) => c.key === sort.key);
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (column?.numeric) return ((Number(a[sort.key]) || 0) - (Number(b[sort.key]) || 0)) * factor;
      return String(a[sort.key] || '').localeCompare(String(b[sort.key] || '')) * factor;
    });
  }, [rows, sort]);

  const chartData = useMemo(
    () =>
      [...rows]
        .sort((a, b) => (Number(b.total_calls) || 0) - (Number(a.total_calls) || 0))
        .slice(0, 12)
        .map((r) => ({
          name: String(r.name || '-').split(' ')[0],
          Connected: Number(r.connected) || 0,
          Missed: Number(r.missed) || 0,
        })),
    [rows],
  );

  const colors = getChartColors(isDark);
  const axisTick = { fill: colors.axis, fontSize: 11 };
  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

  return (
    <Panel className="p-3.5">
      {totals ? (
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <TotalTile label="Total calls" value={totals.total} />
          <TotalTile label="Connected" value={totals.connected} tone="good" />
          <TotalTile label="Missed" value={totals.missed} tone="warn" />
          <TotalTile label="Inbound" value={totals.inbound} tone="call" />
        </div>
      ) : null}

      {rows.length === 0 ? (
        <EmptyNote>No agent call counters reported yet today.</EmptyNote>
      ) : (
        <>
          <div className={`mb-3 rounded-xl border bg-card p-2 ${HAIRLINE}`}>
            <div className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-[0.11em] text-muted-foreground">
              Outcomes per agent {rows.length > 12 ? '· top 12 by volume' : ''}
            </div>
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray="2 4" stroke={colors.grid} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tickMargin={8} tick={axisTick} interval={0} />
                <YAxis axisLine={false} tickLine={false} width={32} allowDecimals={false} tick={axisTick} />
                <Tooltip
                  cursor={{ fill: colors.grid, opacity: 0.4 }}
                  contentStyle={{
                    background: colors.surface,
                    border: 'none',
                    borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(15,23,42,0.16)',
                    fontSize: 11,
                  }}
                  labelStyle={{ color: colors.strong, fontWeight: 600, fontSize: 11 }}
                />
                <Bar dataKey="Connected" stackId="o" fill={colors.outcome3[0]} barSize={18} />
                <Bar dataKey="Missed" stackId="o" fill={colors.outcome3[1]} radius={[3, 3, 0, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className={`overflow-hidden rounded-xl border ${HAIRLINE}`}>
            <TableShell maxH={420}>
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <Th key={c.key} align={c.align}>
                      <button
                        type="button"
                        onClick={() => toggleSort(c.key)}
                        className="inline-flex items-center gap-1 uppercase tracking-[0.08em] hover:text-foreground"
                      >
                        {c.label}
                        {sort.key === c.key ? (
                          sort.dir === 'asc' ? (
                            <ArrowUp size={10} />
                          ) : (
                            <ArrowDown size={10} />
                          )
                        ) : null}
                      </button>
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.id} className="transition-colors hover:bg-muted/40">
                    <Td>
                      <span className="truncate text-[12px] font-medium">{r.name || '—'}</span>
                    </Td>
                    <Td align="right" className="font-semibold tabular-nums">
                      {nf(r.total_calls)}
                    </Td>
                    <Td align="right" className="tabular-nums text-muted-foreground">
                      {nf(r.inbound)}
                    </Td>
                    <Td align="right" className="tabular-nums text-muted-foreground">
                      {nf(r.outbound)}
                    </Td>
                    <Td align="right" className={`tabular-nums ${TONE.good.text}`}>
                      {nf(r.connected)}
                    </Td>
                    <Td align="right" className={`tabular-nums ${TONE.warn.text}`}>
                      {nf(r.missed)}
                    </Td>
                    <Td>
                      <MiniBar
                        value={r.connectPct || 0}
                        tone={connectTone(r.connectPct)}
                        width={44}
                        showValue={r.connectPct == null ? '—' : `${r.connectPct}%`}
                      />
                    </Td>
                    <Td align="right" className="tabular-nums text-muted-foreground">
                      {r.avg_handle || '—'}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
          </div>
        </>
      )}
    </Panel>
  );
}
